import {
  JITEN_FIELD_CODE_PREFIX,
  RESTRICTED_KYOYU_GROUP_CODES,
  SYOKEN_FIELD_CODE_PREFIX,
} from '../consistents';

/**
 * Issue 6212: 特定の共有グループでは商圏・自店系の表示項目・タブなどを抑止する。
 * 判定と一覧フィルタは純粋関数に集約し、画面側は kyoyuGroupCode を渡すだけにする。
 */

/** 表示項目パネルから除外するタブのラベル */
export const COLS_OPTION_PANEL_HIDDEN_GENRE_SYOKEN_TAB = '商圏' as const;

/** ログインユーザーの共有グループが、商圏・自店系の非表示対象かを判定する。 */
export function isKyoyuGroupWithHiddenSyokenJitenFields(
  kyoyuGroupCode: string | undefined
): boolean {
  if (kyoyuGroupCode == null) {
    return false;
  }
  return RESTRICTED_KYOYU_GROUP_CODES.some((code) => code === kyoyuGroupCode);
}

/** 制限対象共有グループで一覧から除く項目コードか（自店 `jiten` / 商圏 `syoken` 接頭辞）。 */
export function fieldCodeHasRestrictedKyoyuGroupPrefix(code: string): boolean {
  return (
    code.startsWith(JITEN_FIELD_CODE_PREFIX) ||
    code.startsWith(SYOKEN_FIELD_CODE_PREFIX)
  );
}

/**
 * お気に入り等に保存された項目コード配列から、制限対象の接頭辞付きコードを除く。
 * 全件が除外対象のみだった場合は undefined（API デフォルトに委ねる）を返す。
 */
export function stripRestrictedKyoyuGroupFieldCodes(
  codes: string[] | undefined
): string[] | undefined {
  if (codes == null) {
    return undefined;
  }
  const filtered = codes.filter(
    (code) => !fieldCodeHasRestrictedKyoyuGroupPrefix(code)
  );
  if (filtered.length === codes.length) {
    return codes;
  }
  return filtered.length > 0 ? filtered : undefined;
}

/** 表示項目一覧から、制限共有グループ向けに自店・商圏接頭辞の項目を除く。 */
export function filterRestrictedKyoyuGroupPrefixedFields<
  T extends { code: string }
>(fields: T[], kyoyuGroupCode: string | undefined): T[] {
  if (!isKyoyuGroupWithHiddenSyokenJitenFields(kyoyuGroupCode)) {
    return fields;
  }
  return fields.filter(
    (field) => !fieldCodeHasRestrictedKyoyuGroupPrefix(field.code)
  );
}

/** 表示項目パネルのジャンル一覧から「商圏」ボタンを除く（制限共有グループのみ）。 */
export function filterGenresForRestrictedKyoyuGroup(
  genres: string[] | undefined,
  kyoyuGroupCode: string | undefined
): string[] | undefined {
  if (
    !isKyoyuGroupWithHiddenSyokenJitenFields(kyoyuGroupCode) ||
    genres == null
  ) {
    return genres;
  }
  return genres.filter(
    (genre) => genre !== COLS_OPTION_PANEL_HIDDEN_GENRE_SYOKEN_TAB
  );
}

/** 表示項目定義から商圏列（isSyoken）を除く。 */
export function filterSyokenFields<T extends { isSyoken: boolean }>(
  fields: T[],
  kyoyuGroupCode: string | undefined
): T[] {
  if (!isKyoyuGroupWithHiddenSyokenJitenFields(kyoyuGroupCode)) {
    return fields;
  }
  return fields.filter((field) => field.isSyoken !== true);
}

/** テーブル列定義から商圏列（isSyoken）を除く。 */
export function filterSyokenColumns<T extends { isSyoken?: boolean }>(
  columns: T[],
  kyoyuGroupCode: string | undefined
): T[] {
  if (!isKyoyuGroupWithHiddenSyokenJitenFields(kyoyuGroupCode)) {
    return columns;
  }
  return columns.filter((column) => column.isSyoken !== true);
}
